import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment } from 'react';
import { pathgen } from 'object-path-generator';
import { ConditionalWrapper, FullRow, TableList, Tooltip, Typography, useDevice } from 'seagull';
import { emptyCell } from '~/constants';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { AccountingMappingIcon } from '~/integrations/Accounting/AccountingMappingIcon';
import { invoiceModel } from '~/models';
import { Accounting } from '~/types/api.model';
import TimeActivityTooltip from '../../TimeActivityTooltip/TimeActivityTooltip';
import styles from '../FullPageInvoice.module.scss';
import { getItemQBOTooltip, getXeroTooltip, MobileDivider } from '../FullPageInvoice.utils';
import InvoiceSubLineItems from './InvoiceSubLineItems';
export const InvoiceLineItemsTableTestIds = pathgen('InvoiceLineItemsTableTestIds');
const firstColumnWidth = 400;
const columnDefs = [
    {
        width: firstColumnWidth,
        value: 'Description',
        mobile: {
            label: {
                hideColon: true
            }
        }
    },
    {
        width: 30,
        value: '',
        mobile: {
            hide: true
        }
    },
    {
        width: 30,
        value: '',
        mobile: {
            hide: true
        }
    },
    {
        width: [
            50,
            60
        ],
        hAlign: 'end',
        value: 'Quantity',
        mobile: {
            label: {
                text: 'Quantity',
                hideColon: true
            },
            spaceBetween: true
        }
    },
    {
        width: [
            112,
            120
        ],
        hAlign: 'end',
        value: 'Rate',
        mobile: {
            label: {
                text: 'Rate',
                hideColon: true
            },
            spaceBetween: true
        }
    },
    {
        width: [
            80,
            90
        ],
        hAlign: 'end',
        value: 'Discount',
        mobile: {
            label: {
                text: 'Discount',
                hideColon: true
            },
            spaceBetween: true
        }
    },
    {
        width: [
            88,
            104
        ],
        hAlign: 'end',
        value: 'Total',
        mobile: {
            spaceBetween: true,
            divider: true,
            label: {
                text: 'Total',
                hideColon: true,
                weight: 'normal',
                skin: 'standard'
            },
            value: {
                weight: 'normal',
                skin: 'standard'
            }
        }
    }
];
export function qboIndicatorCell(param) {
    let { isGrouped, qbo, isVendor, getRowTestId } = param;
    return qbo && isVendor ? {
        value: /*#__PURE__*/ _jsx(Typography, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.qboCellContainer,
                children: [
                    qbo.timeActivity && /*#__PURE__*/ _jsx("div", {
                        "data-testid": getRowTestId('time-activity-linked'),
                        children: /*#__PURE__*/ _jsx(TimeActivityTooltip, {
                            timeActivity: qbo.timeActivity,
                            grouped: isGrouped
                        })
                    }),
                    /*#__PURE__*/ _jsx(Tooltip, {
                        title: getItemQBOTooltip(qbo, getRowTestId('sync-tooltip')),
                        children: /*#__PURE__*/ _jsx("div", {
                            "data-testid": getRowTestId('sync'),
                            children: /*#__PURE__*/ _jsx(AccountingMappingIcon, {
                                size: 18
                            })
                        })
                    })
                ]
            })
        })
    } : emptyCell;
}
function xeroIndicatorCell(param) {
    let { item, isVendor, getRowTestId } = param;
    return item.xero && isVendor ? {
        value: /*#__PURE__*/ _jsx(Typography, {
            children: /*#__PURE__*/ _jsx(Tooltip, {
                title: getXeroTooltip({
                    accounting: Accounting.Xero,
                    itemName: item.xero.item.name,
                    testId: getRowTestId('xero-sync-tooltip')
                }),
                children: /*#__PURE__*/ _jsx("div", {
                    className: styles.xeroCellContainer,
                    "data-testid": getRowTestId('xero-sync'),
                    children: /*#__PURE__*/ _jsx(AccountingMappingIcon, {
                        size: 18
                    })
                })
            })
        })
    } : emptyCell;
}
export default function InvoiceLineItemsTable(props) {
    const { invoice, isVendor } = props;
    const { SERVICE_TEMPLATE_BUNDLE } = useFeatureFlags();
    const { isMobileDevice } = useDevice();
    const aCell = (contents, testId)=>({
            value: contents ? /*#__PURE__*/ _jsx(Typography, {
                testId: testId,
                children: contents
            }) : null
        });
    return /*#__PURE__*/ _jsx(TableList.Provider, {
        variant: "minimal",
        columnDefs: columnDefs,
        justifyBetween: true,
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.table,
            children: [
                /*#__PURE__*/ _jsx(TableList.Header, {
                    className: styles.tableHeader,
                    bgColor: "darkGray",
                    items: columnDefs
                }),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        invoice.items.map((item, idx)=>{
                            const getRowTestId = (suffix)=>"fpi-row-".concat(idx, "-").concat(suffix);
                            const subItems = item.subItems;
                            return /*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(TableList.Row, {
                                        size: "small",
                                        mobile: {
                                            skin: 'mobileGray'
                                        },
                                        items: [
                                            aCell(/*#__PURE__*/ _jsx(ConditionalWrapper, {
                                                condition: isMobileDevice,
                                                wrapper: (children)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Typography, {
                                                                variant: "h6",
                                                                weight: "normal",
                                                                children: children
                                                            }),
                                                            item.note && /*#__PURE__*/ _jsx(Typography, {
                                                                children: item.note
                                                            })
                                                        ]
                                                    }),
                                                children: /*#__PURE__*/ _jsx(_Fragment, {
                                                    children: invoiceModel.itemName(item)
                                                })
                                            }), getRowTestId('name')),
                                            qboIndicatorCell({
                                                isGrouped: invoiceModel.isGrouped(item),
                                                qbo: item.qbo,
                                                isVendor,
                                                getRowTestId
                                            }),
                                            xeroIndicatorCell({
                                                item,
                                                getRowTestId,
                                                isVendor
                                            }),
                                            aCell(invoiceModel.itemQuantity(item), getRowTestId('quantity')),
                                            aCell(invoiceModel.itemRate(item), getRowTestId('rate')),
                                            aCell(invoiceModel.itemDiscount(item), getRowTestId('discount')),
                                            aCell(invoiceModel.itemTotalAmount(item), getRowTestId('total'))
                                        ]
                                    }),
                                    !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            item.note && /*#__PURE__*/ _jsx(Typography, {
                                                testId: getRowTestId('note'),
                                                skin: "secondary",
                                                variant: "paragraph2",
                                                className: styles.note,
                                                style: {
                                                    width: firstColumnWidth
                                                },
                                                children: item.note
                                            }),
                                            subItems && isVendor && SERVICE_TEMPLATE_BUNDLE && subItems.map((subItem, subItemIdx)=>/*#__PURE__*/ _jsx(InvoiceSubLineItems, {
                                                    isVendor: isVendor,
                                                    subItem: subItem,
                                                    quantity: invoiceModel.itemQuantity(item),
                                                    disableMarginBottom: subItemIdx !== subItems.length - 1
                                                }, subItemIdx))
                                        ]
                                    })
                                ]
                            }, item.id);
                        }),
                        isMobileDevice ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(MobileDivider, {}),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "h6",
                                    weight: "normal",
                                    testId: "fpi-mobile-total",
                                    children: /*#__PURE__*/ _jsxs(FullRow, {
                                        justifyContent: "space-between",
                                        padding: "0 8px",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Total"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: invoiceModel.totalAmount(invoice)
                                            })
                                        ]
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(TableList.Row, {
                            className: styles.finalRow,
                            size: "large",
                            items: [
                                emptyCell,
                                emptyCell,
                                emptyCell,
                                emptyCell,
                                aCell('Total'),
                                aCell(invoiceModel.totalAmount(invoice))
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
